import { useEffect, useState } from "react";
import { api } from "../lib/api";
import AdminShell from "../components/AdminShell";
import { MessageCircle, Loader2, Search, AlertTriangle, ArrowLeft } from "lucide-react";

const fmtTime = (iso) => {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "numeric", month: "short", hour: "2-digit", minute: "2-digit",
    });
  } catch (_) { return iso; }
};

function ConversationItem({ c, active, onClick }) {
  return (
    <button
      data-testid={`david-chat-item-${c.session_id}`}
      onClick={onClick}
      className={"w-full rounded-xl border p-3 text-left transition-colors " +
        (active ? "border-[#0A8B85] bg-[#0A8B85]/5" : "border-neutral-200 bg-white hover:border-[#0A8B85]/40")}
    >
      <div className="flex items-center justify-between gap-2">
        <p className="truncate text-sm font-semibold text-neutral-900">
          {c.name || c.email || c.user_id}
        </p>
        <span className="whitespace-nowrap text-[11px] text-neutral-400">{fmtTime(c.last_at)}</span>
      </div>
      {(c.company || c.email) && (
        <p className="mt-0.5 truncate text-[11px] text-neutral-500">
          {[c.company, c.email].filter(Boolean).join(" · ")}
        </p>
      )}
      <p className="mt-1.5 truncate text-xs text-neutral-600">{c.preview || "—"}</p>
      <div className="mt-2 flex items-center gap-2 text-[11px] text-neutral-500">
        <span>{c.message_count} messages</span>
        {c.stuck_count > 0 && (
          <span data-testid={`david-chat-stuck-${c.session_id}`}
                className="inline-flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 font-semibold text-amber-800 ring-1 ring-amber-200">
            <AlertTriangle className="h-3 w-3" /> {c.stuck_count} stuck
          </span>
        )}
      </div>
    </button>
  );
}

function Transcript({ sessionId, onBack }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/admin/david/conversations/${sessionId}`)
      .then((r) => setData(r.data))
      .catch(() => setData(null))
      .finally(() => setLoading(false));
  }, [sessionId]);

  if (loading) {
    return <div className="flex items-center justify-center py-20 text-neutral-400"><Loader2 className="h-5 w-5 animate-spin" /></div>;
  }
  if (!data) return <p className="p-6 text-sm text-neutral-500">Couldn't load this conversation.</p>;

  return (
    <div data-testid="david-chat-transcript">
      <div className="flex items-center gap-3 border-b border-neutral-100 pb-3">
        <button onClick={onBack} data-testid="david-transcript-back"
          className="rounded-lg border border-neutral-200 p-1.5 text-neutral-600 hover:bg-neutral-50 lg:hidden">
          <ArrowLeft className="h-4 w-4" />
        </button>
        <div className="min-w-0">
          <p className="truncate text-sm font-bold text-neutral-900">{data.name || data.email || data.user_id}</p>
          <p className="truncate text-xs text-neutral-500">{[data.company, data.email].filter(Boolean).join(" · ")}</p>
        </div>
      </div>
      <div className="mt-4 space-y-3">
        {data.messages.map((m, i) => (
          <div key={i} className={"flex " + (m.role === "user" ? "justify-end" : "justify-start")}>
            <div className={"max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed " +
              (m.role === "user"
                ? "rounded-br-sm bg-[#0A8B85] text-white"
                : "rounded-bl-sm border border-neutral-200 bg-neutral-50 text-neutral-800")}>
              <p className="whitespace-pre-wrap break-words">{m.text}</p>
              <p className={"mt-1 text-[10px] " + (m.role === "user" ? "text-white/60" : "text-neutral-400")}>
                {m.role === "user" ? "Founder" : "David"} · {fmtTime(m.ts)}
                {m.role === "assistant" && m.confident === false && (
                  <span className="ml-1.5 font-semibold text-amber-600">· not confident</span>
                )}
              </p>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function AdminDavidChats() {
  const [convos, setConvos] = useState(null);
  const [q, setQ] = useState("");
  const [selected, setSelected] = useState(null);

  const load = (query) => {
    api.get("/admin/david/conversations", { params: query ? { q: query } : {} })
      .then((r) => setConvos(r.data.conversations))
      .catch(() => setConvos([]));
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = setTimeout(() => load(q.trim() || undefined), 350);
    return () => clearTimeout(t);
  }, [q]);

  return (
    <AdminShell>
      <div data-testid="david-chats-page">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex items-center gap-2.5">
            <div className="flex h-9 w-9 items-center justify-center rounded-lg bg-[#0A8B85]/10">
              <MessageCircle className="h-5 w-5 text-[#0A8B85]" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-neutral-900">David conversations</h1>
              <p className="text-xs text-neutral-500">What founders are asking the AI advisor</p>
            </div>
          </div>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-neutral-400" />
            <input
              data-testid="david-chat-search"
              placeholder="Search email, name, company…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-64 rounded-lg border border-neutral-200 bg-white py-2 pl-9 pr-3 text-sm outline-none focus:border-[#0A8B85]"
            />
          </div>
        </div>

        <div className="mt-6 grid gap-5 lg:grid-cols-[360px,1fr]">
          <div className={"space-y-2 " + (selected ? "hidden lg:block" : "")}>
            {!convos ? (
              <div className="flex items-center justify-center py-16 text-neutral-400"><Loader2 className="h-5 w-5 animate-spin" /></div>
            ) : convos.length === 0 ? (
              <p className="rounded-xl border border-neutral-200 bg-white p-6 text-sm text-neutral-500" data-testid="david-chats-empty">
                No conversations yet.
              </p>
            ) : (
              convos.map((c) => (
                <ConversationItem key={c.session_id} c={c}
                  active={selected === c.session_id}
                  onClick={() => setSelected(c.session_id)} />
              ))
            )}
          </div>
          <div className={"rounded-2xl border border-neutral-200 bg-white p-5 " + (selected ? "" : "hidden lg:block")}>
            {selected ? (
              <Transcript sessionId={selected} onBack={() => setSelected(null)} />
            ) : (
              <p className="py-20 text-center text-sm text-neutral-400">Select a conversation to read the transcript.</p>
            )}
          </div>
        </div>
      </div>
    </AdminShell>
  );
}
